import { SingleShippingEmissionEstimate } from '@lune-climate/lune'
import { SuccessResponseWithMeta } from '@lune-climate/lune/esm/core/SuccessResponse'
import { CalculationResults, Leg, resolvedLegToApiLeg } from '@lune-fe/lune-components-lib'
import { Stack } from '@mui/material'
import { FC, RefObject, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { asSuccessResponseWithMeta } from 'endpoints/util'
import { useLuneClient } from 'hooks/useLuneClient'
import useTitle from 'hooks/useTitle'
import useToken from 'hooks/useToken'
import { EstimateTypeEnum, EstimateTypeName, ResultsWrapper } from 'views/CalculateEmissions'
import SingleLegEstimate from 'views/CalculateEmissions/Logistics/SingleLegEstimate'
import MultiLegEstimate from 'views/CalculateEmissions/MultiLegLogistics/MultiLegEstimate'

const LogisticsResults: FC<{ scrollRef: RefObject<HTMLDivElement> }> = ({ scrollRef }) => {
    useTitle('Logistics Emissions Results')
    const { id } = useParams<{ id: string }>()
    const { token } = useToken()
    const [estimate, setEstimate] =
        useState<SuccessResponseWithMeta<SingleShippingEmissionEstimate>>()
    const [resolvedLegs, setResolvedLegs] = useState<Leg[]>([])
    const luneClient = useLuneClient()

    useEffect(() => {
        if (id) {
            luneClient.getShippingEstimate(id).then((result) => {
                if (result.isOk()) {
                    const estimate = asSuccessResponseWithMeta<SingleShippingEmissionEstimate>(
                        result.value,
                    )
                    if (estimate.resolvedLegs) {
                        setResolvedLegs(
                            estimate.resolvedLegs.map((resolvedLeg) => {
                                return resolvedLegToApiLeg(
                                    resolvedLeg,
                                    result.value.convertedShipment,
                                    result.value.shipmentConversionMethod,
                                )
                            }),
                        )
                    }
                }
                return result.map((v) => setEstimate(asSuccessResponseWithMeta(v)))
            })
        }
    }, [id, luneClient])

    return (
        <ResultsWrapper
            scrollRef={scrollRef}
            loading={!estimate?.request}
            calculatedAmount={estimate?.mass.amount!}
            estimateId={estimate?.id}
            token={token}
            isMultiLeg={false}
        >
            {estimate && (
                <>
                    <CalculationResults
                        title={estimate.request.name || EstimateTypeName(EstimateTypeEnum.SHIPPING)}
                        amount={estimate.mass.amount}
                    />
                    <Stack direction={'column'} spacing={9} mt={9}>
                        {resolvedLegs.length ? (
                            <MultiLegEstimate
                                legs={resolvedLegs}
                                load={estimate.request.shipment}
                                meta={estimate._meta}
                                source={estimate.source}
                                requestRoute={
                                    'route' in estimate.request ? estimate.request.route : undefined
                                }
                            />
                        ) : (
                            <SingleLegEstimate estimate={estimate} />
                        )}
                    </Stack>
                </>
            )}
        </ResultsWrapper>
    )
}

export default LogisticsResults
